import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Grid,
  Select,
  Skeleton,
  Switch,
  Text,
  Tooltip,
} from '@chakra-ui/react'
import type {
  AcquisitionNumber,
  DetailedUser,
  EditUser,
  OrganizationUnit,
  UserProperties
} from 'BrokerTsLib/src/dtos'
import {
  BadRequestError
} from 'BrokerTsLib/src/dtos'
import type { LdapUser } from 'brokertslib/src/dtos/User/LdapUser'
import { UserRole } from 'BrokerTsLib/src/dtos/User/UserRole'
import { Insurer, UserType, UserTypes } from 'brokertslib/src/enums'
import { useGetAvailableInsurers } from 'BrokerTsLib/src/hooks/useOrganizationUnit'
import {
  useCheckUserNameExists,
  useGetRoles,
} from 'BrokerTsLib/src/hooks/useUser'
import {
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from 'BrokerTsLib/src/rhf'
import { setModelStateErrors } from 'BrokerTsLib/src/Utils/formModelState'
import { IsSupervisor } from 'brokertslib/src/Utils/organizationUnitUtils'
import { Fragment, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDebounce } from 'use-debounce'

import ErrorSummary from '@/components/ErrorSummary'
import { FormGroup } from '@/components/FormGroup'
import { FormLabel } from '@/components/FormLabel'
import {
  FormSection,
  FormSectionBody,
  FormSectionDescription,
  FormSectionHeader,
  FormSectionTitle,
} from '@/components/FormSection'
import { InputGroup } from '@/components/InputGroup'
import { PhoneInputGroup } from '@/components/PhoneInputGroup'
import { SwitchControl } from '@/components/SwitchControl'
import { useAuth } from '@/context/AuthContext'
import { ArrowLeft } from '@/icons'

import { isIn } from 'BrokerTsLib/src/Utils/comparisonUtils'
import { LoadLdapUserButton } from './containers/LoadLdapUserButton'

export const UserEditForm = ({
  user,
  properties,
  onSubmit,
  isCreate = false,
  organization,
}: {
  user: DetailedUser
  properties: UserProperties
  onSubmit: (
    values: EditUser,
  ) => Promise<void | BadRequestError<EditUser> | Error>
  isCreate?: boolean
  organization: OrganizationUnit
}) => {
  const auth = useAuth()
  const navigate = useNavigate()
  const methods = useForm<EditUser>({
    defaultValues: {
      ...user,
      personalNumbers: user.personalNumbers
        ? user.personalNumbers.slice().sort((a, b) =>
          a.insurerName.localeCompare(b.insurerName),
        )
        : [],
      properties: { ...properties },
    },
    mode: 'onBlur',
  })

  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting, touchedFields },
    setValue,
    setError,
    watch,
    control,
    trigger,
    reset
  } = methods

  const role = watch('roles.0.role', undefined)

  const hasSpecificRole = useMemo(
    () =>
      role && isIn(role, UserRole.SubstituteBusinessman, UserRole.Tipster),
    [role],
  )
  useEffect(() => {
    if (hasSpecificRole) {
      trigger()
      setValue('userTypeId', UserType.ApplicationUser)
    }
  }, [hasSpecificRole])

  const submitHandler = (data: EditUser) =>
    onSubmit(data).then((response) => {
      if (response instanceof BadRequestError)
        setModelStateErrors<EditUser>(setError, response)
    })

  const userName = watch('userName')

  const insertLdapUser = (ldapUser: LdapUser) => {
    setValue('firstName', ldapUser.firstName, { shouldValidate: true })
    setValue('lastName', ldapUser.lastName, { shouldValidate: true })
    setValue('email', ldapUser.email, { shouldValidate: true })
    setValue('userNameOtherSystem', ldapUser.userNameOtherSystem, {
      shouldValidate: true,
    })
    setValue('userTypeId', UserType.ActiveDirectoryUser, {
      shouldValidate: true,
    })
  }

  const { fields } = useFieldArray({
    name: 'personalNumbers',
    control,
  })

  const [userNameDebounced] = useDebounce(userName, 500)
  const { data: userNameExistsData } = useCheckUserNameExists(
    userNameDebounced,
    {
      enabled:
        !hasSpecificRole &&
        userNameDebounced?.length > 0 &&
        userNameDebounced !== user.userName,
    },
  )
  const userNameExists = useMemo(
    () =>
      !hasSpecificRole &&
      userNameDebounced !== user.userName &&
      (userNameExistsData?.data ?? false),
    [hasSpecificRole, userNameDebounced, userNameExistsData, user.userName],
  )

  useEffect(() => {
    if (touchedFields.userName)
      trigger('userName')
  }, [touchedFields, userNameExists, trigger])

  const registerUserName = register('userName', {
    required: hasSpecificRole ? undefined : 'Zadejte uživatelské jméno',
    maxLength: 256,
    validate: () =>
      userNameExists ? 'Uživatelské jméno již existuje' : undefined,
  })

  const personalNumbers = watch('personalNumbers')
  const { data: insurersData } = useGetAvailableInsurers(user.organizationUnitId)
  useEffect(() => {
    if (insurersData?.data) {
      const personalNumbersAvailableInsurers: AcquisitionNumber[] = insurersData?.data.map(i => ({
        insurerId: i.insurerId,
        insurerName: i.insurer,
        acquisitionNumber: personalNumbers?.find(p => p.insurerId === i.insurerId)?.acquisitionNumber ?? ''
      }))
      // We want to also show already assigned personal numbers for insurers that are not available
      const personalNumbersAll = personalNumbersAvailableInsurers.concat(
        personalNumbers.filter(p => !personalNumbersAvailableInsurers.some(p2 => p2.insurerId === p.insurerId))
      )
      setValue('personalNumbers', personalNumbersAll.slice().sort((a, b) => a.insurerName.localeCompare(b.insurerName)))
    }
  }, [insurersData])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(submitHandler)}>
        {hasSpecificRole ? (
          <FormSection>
            <FormSectionHeader>
              <FormSectionTitle>Informace o uživateli</FormSectionTitle>

              <FormSectionDescription>
                Základní informace o uživateli.
              </FormSectionDescription>
            </FormSectionHeader>

            <FormSectionBody>
              <InputGroup
                hidden
                {...register('organizationUnitId', { valueAsNumber: true })}
              />
              <RoleFormControl isCreate={isCreate} user={user} />

              <FormGroup>
                <FormControl isRequired isInvalid={!!errors.firstName}>
                  <FormLabel>Jméno</FormLabel>
                  <InputGroup
                    id="firstName"
                    placeholder="Vaše jméno"
                    errors={errors.firstName}
                    {...register('firstName', {
                      required: 'Zadejte jméno',
                      maxLength: 100,
                    })}
                  />
                </FormControl>

                <FormControl isRequired isInvalid={!!errors.lastName}>
                  <FormLabel>Příjmení</FormLabel>
                  <InputGroup
                    id="lastName"
                    placeholder="Vaše příjmení"
                    errors={errors.lastName}
                    {...register('lastName', {
                      required: 'Zadejte příjmení',
                      maxLength: 100,
                    })}
                  />
                </FormControl>

                <FormControl
                  isRequired
                  isInvalid={!!errors.userNameOtherSystem}
                >
                  <FormLabel htmlFor="userNameOtherSystem">
                    Osobní číslo
                  </FormLabel>
                  <InputGroup
                    id="userNameOtherSystem"
                    placeholder="Osobní číslo"
                    errors={errors.userNameOtherSystem}
                    {...register('userNameOtherSystem', {
                      required: hasSpecificRole
                        ? 'Zadejte osobní číslo zaměstnance'
                        : undefined,
                      maxLength: 256,
                    })}
                  />
                </FormControl>
              </FormGroup>
            </FormSectionBody>
          </FormSection>
        ) : (
          <>
            <FormSection>
              <FormSectionHeader>
                <FormSectionTitle>Informace o uživateli</FormSectionTitle>

                <FormSectionDescription>
                  Základní informace o uživateli.
                </FormSectionDescription>
              </FormSectionHeader>

              <FormSectionBody gap={6}>
                <InputGroup
                  hidden
                  {...register('organizationUnitId', { valueAsNumber: true })}
                />
                <FormGroup sx={{ columnGap: 4, alignItems: 'flex-start' }}>
                  <RoleFormControl isCreate={isCreate} user={user} />
                  <FormControl isInvalid={!!errors.userNameOtherSystem}>
                    <FormLabel htmlFor="userNameOtherSystem">
                      Číslo zaměstnance
                    </FormLabel>
                    <InputGroup
                      id="userNameOtherSystem"
                      placeholder="Číslo zaměstnance"
                      errors={errors.userNameOtherSystem}
                      {...register('userNameOtherSystem', {
                        maxLength: 256,
                      })}
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl isRequired isInvalid={!!errors.userTypeId}>
                    <FormLabel>Typ uživatele</FormLabel>

                    <Select
                      {...register('userTypeId', {
                        required: 'Vyberte typ uživatele',
                      })}
                    >
                      {isCreate && (
                        <option value={''}>Vyberte typ uživatele</option>
                      )}
                      {UserTypes.map(([type, name], i) => (
                        <option key={i} value={type}>
                          {name}
                        </option>
                      ))}
                    </Select>

                    <FormErrorMessage>
                      {errors.userTypeId?.message}
                    </FormErrorMessage>
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.userName}>
                    <FormLabel htmlFor="username">Uživatelské jméno</FormLabel>
                    <InputGroup
                      id="username"
                      placeholder="Uživatelské jméno"
                      errors={errors.userName}
                      {...registerUserName}
                      onInput={registerUserName && registerUserName.onChange}
                    />
                  </FormControl>

                  <LoadLdapUserButton
                    userName={userName}
                    onSubmit={insertLdapUser}
                  />
                </FormGroup>

                <FormGroup>
                  <FormControl isRequired isInvalid={!!errors.firstName}>
                    <FormLabel>Jméno</FormLabel>
                    <InputGroup
                      id="firstName"
                      placeholder="Vaše jméno"
                      errors={errors.firstName}
                      {...register('firstName', {
                        required: 'Zadejte jméno',
                        maxLength: 100,
                      })}
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.lastName}>
                    <FormLabel>Příjmení</FormLabel>
                    <InputGroup
                      id="lastName"
                      placeholder="Vaše příjmení"
                      errors={errors.lastName}
                      {...register('lastName', {
                        required: 'Zadejte příjmení',
                        maxLength: 100,
                      })}
                    />
                  </FormControl>
                </FormGroup>

                <FormGroup>
                  <FormControl>
                    <FormLabel>Ulice</FormLabel>

                    <InputGroup
                      placeholder="Např. Dlouhá"
                      htmlSize={24}
                      errors={errors.addressStreet}
                      {...register('addressStreet', { maxLength: 100 })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Číslo popisné a orientační</FormLabel>

                    <InputGroup
                      placeholder="Např. 123"
                      htmlSize={8}
                      errors={errors.addressStreetNumber}
                      {...register('addressStreetNumber', { maxLength: 10 })}
                    />
                  </FormControl>
                </FormGroup>
                <FormGroup>
                  <FormControl>
                    <FormLabel>PSČ</FormLabel>

                    <InputGroup
                      placeholder="Např. 110 00"
                      pattern="[\d\s]+"
                      htmlSize={8}
                      errors={errors.addressPostCode}
                      {...register('addressPostCode', { maxLength: 20 })}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Město</FormLabel>

                    <InputGroup
                      placeholder="Např. Praha"
                      htmlSize={24}
                      errors={errors.addressCity}
                      {...register('addressCity', { maxLength: 100 })}
                    />
                  </FormControl>
                </FormGroup>

                <FormGroup>
                  <FormControl>
                    <FormLabel>Mobilní telefon</FormLabel>

                    <PhoneInputGroup
                      errors={errors.phoneNumber}
                      input={register('phoneNumber', { maxLength: 256 })}
                    />
                  </FormControl>

                  <FormControl isRequired isInvalid={!!errors.email}>
                    <FormLabel>E-mail</FormLabel>

                    <InputGroup
                      type="email"
                      errors={errors.email}
                      {...register('email', {
                        required: 'Zadejte emailovou adresu',
                        maxLength: 256,
                      })}
                    />
                  </FormControl>
                </FormGroup>

                <FormGroup>
                  <FormControl>
                    <FormLabel>Aktivní</FormLabel>

                    <SwitchControl>
                      <FormLabel htmlFor="active" variant="switch">
                        Ne
                      </FormLabel>
                      <Switch id="active" size="md" {...register('valid')} />
                      <FormLabel htmlFor="active" variant="switch">
                        Ano
                      </FormLabel>
                    </SwitchControl>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Pojištění povoleno</FormLabel>

                    <SwitchControl>
                      <FormLabel htmlFor="insuringAllowed" variant="switch">
                        Ne
                      </FormLabel>
                      <Switch
                        id="insuringAllowed"
                        size="md"
                        {...register('insuringAllowed')}
                      />
                      <FormLabel htmlFor="insuringAllowed" variant="switch">
                        Ano
                      </FormLabel>
                    </SwitchControl>
                  </FormControl>

                  <FormControl>
                    <FormLabel>Sjednání za jiného uživatele</FormLabel>

                    <SwitchControl>
                      <FormLabel
                        htmlFor="allowCreateInsuranceAsSomeoneElse"
                        variant="switch"
                      >
                        Ne
                      </FormLabel>
                      <Switch
                        id="allowCreateInsuranceAsSomeoneElse"
                        size="md"
                        {...register(
                          'properties.allowCreateInsuranceAsSomeoneElse',
                        )}
                      />
                      <FormLabel
                        htmlFor="allowCreateInsuranceAsSomeoneElse"
                        variant="switch"
                      >
                        Ano
                      </FormLabel>
                    </SwitchControl>
                  </FormControl>
                </FormGroup>

                {auth?.user?.adminCebiaRights && IsSupervisor(organization) && (
                  <FormGroup>
                    <FormControl>
                      <FormLabel>Rtpc oprávnění</FormLabel>

                      <SwitchControl>
                        <FormLabel htmlFor="rtpcRights" variant="switch">
                          Ne
                        </FormLabel>
                        <Switch
                          id="rtpcRights"
                          size="md"
                          {...register('properties.rtpcRights')}
                        />
                        <FormLabel htmlFor="rtpcRights" variant="switch">
                          Ano
                        </FormLabel>
                      </SwitchControl>
                    </FormControl>
                  </FormGroup>
                )}
              </FormSectionBody>
            </FormSection>
            {fields && fields.length > 0 && (
              <FormSection>
                <FormSectionHeader>
                  <FormSectionTitle>Osobní čísla</FormSectionTitle>

                  <FormSectionDescription>
                    Zadejte osobní čísla uživatele u jednotlivých pojišťoven.
                  </FormSectionDescription>
                </FormSectionHeader>

                <FormSectionBody>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { base: '1fr', md: '1fr 1fr' },
                      columnGap: 16,
                      rowGap: 4,
                      w: { base: 'full', xl: '75%' },
                    }}
                  >
                    {fields.map((number, index) => {
                      const personalNumberProperty =
                        user.personalNumbersProperty
                          ?.map((value, index) => ({ value, index }))
                          .find((x) => x.value.insurerId === number.insurerId)

                      return (
                        <Fragment key={number.insurerId}>
                          <FormControl>
                            {/* InsurersWithRecommendedPersonalNumbers.includes(number.insurerId) */}
                            <FormLabel>
                              <Box>
                                {number.insurerName}
                                {InsurersWithRecommendedPersonalNumbers.includes(
                                  number.insurerId,
                                ) && (
                                  <InsurersWithRecommendedPersonalNumbersIndication />
                                )}
                              </Box>
                            </FormLabel>
                            <InputGroup
                              sx={{ w: 'full' }}
                              id={number.insurerName}
                              errors={
                                errors.personalNumbers?.[index]
                                  ?.acquisitionNumber
                              }
                              {...register(
                                `personalNumbers.${index}.acquisitionNumber`,
                              )}
                            />
                          </FormControl>
                          {personalNumberProperty && (
                            <FormControl>
                              <FormLabel>
                                <Box>
                                  {number.insurerName} (pojištění majetku)
                                  {InsurersWithRecommendedPersonalNumbers.includes(
                                    number.insurerId,
                                  ) && (
                                    <InsurersWithRecommendedPersonalNumbersIndication />
                                  )}
                                </Box>
                              </FormLabel>
                              <InputGroup
                                sx={{ w: 'full' }}
                                id={
                                  personalNumberProperty.value.insurerId +
                                  '_property'
                                }
                                errors={
                                  errors.personalNumbersProperty?.[
                                    personalNumberProperty.index
                                  ]?.acquisitionNumber
                                }
                                {...register(
                                  `personalNumbersProperty.${personalNumberProperty.index}.acquisitionNumber`,
                                )}
                              />
                            </FormControl>
                          )}
                        </Fragment>
                      )
                    })}
                  </Box>
                </FormSectionBody>
              </FormSection>
            )}
          </>
        )}

        <ErrorSummary errors={errors} />

        <Grid
          sx={{
            width: '100%',
            gridTemplateColumns: 'auto auto',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Button
            variant="ghost"
            isLoading={isSubmitting}
            type="button"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft color="secondary.500" />
            Zpět
          </Button>
          <Button
            colorScheme="primary"
            isLoading={isSubmitting}
            px={6}
            onClick={() => {
              handleSubmit(submitHandler)()
            }}
          >
            Uložit
          </Button>
        </Grid>
      </form>
    </FormProvider>
  )
}

const RoleFormControl = ({
  isCreate,
  user,
}: {
  isCreate: boolean
  user: DetailedUser
}) => {
  const {
    register,
    setValue,
    formState: { errors },
    watch,
  } = useFormContext<EditUser>()
  const { data: rolesData, isLoading: loadingRoles } = useGetRoles(user.userId)
  const Roles = useMemo(() => rolesData?.data ?? [], [rolesData])

  const role = watch('roles.0.role', undefined)
  useEffect(() => {
    if (role)
      setValue('roles.0', {
        role,
        roleName: Roles.find((x) => x.role === role)?.roleName ?? '',
      })
  }, [role, Roles])

  return (
    <FormControl isRequired isInvalid={!!errors.roles}>
      <FormLabel>Role</FormLabel>

      <Skeleton minW={'28'} isLoaded={!loadingRoles}>
        <Select
          isInvalid={!!errors.roles}
          {...register('roles.0.role', {
            validate: (value) => (value ? undefined : 'Vyberte roli'),
            valueAsNumber: true,
          })}
        >
          {isCreate && <option value={0}>Vyberte roli</option>}
          {Roles.map((role) => (
            <option key={role.role} value={role.role}>
              {role.roleName}
            </option>
          ))}
        </Select>
      </Skeleton>

      <FormErrorMessage>
        {errors.roles?.at?.(0)?.role?.message}
      </FormErrorMessage>
    </FormControl>
  )
}

const InsurersWithRecommendedPersonalNumbers = [
  Insurer.CPP,
  Insurer.Csob,
  Insurer.Das,
  Insurer.Direct,
  Insurer.Fortegra,
  Insurer.Generali,
  Insurer.GeneraliCeskaPojistovna,
  Insurer.Kooperativa,
  Insurer.Pillow,
  Insurer.Slavia,
  Insurer.Uniqa,
]

const InsurersWithRecommendedPersonalNumbersIndication = () => (
  <Tooltip label="Pojišťovna nebude bez osobního čísla správně fungovat.">
    <Text
      as="span"
      color="green.400"
      fontSize="2xl"
      lineHeight={0}
      verticalAlign="sub"
    >
      {' '}
      *
    </Text>
  </Tooltip>
)
